import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LoadingButton } from "./Spinner";

interface Props {
  collectionId: string;
  onClose: () => void;
}

// Modal for importing / syncing an OpenAPI spec onto a collection. Shows
// drift summary on subsequent visits.
export default function OpenApiImport({ collectionId, onClose }: Props) {
  const qc = useQueryClient();
  const existing = useQuery({
    queryKey: ["openapi", collectionId],
    queryFn: () => api.openapi.get(collectionId),
  });

  const [sourceUrl, setSourceUrl] = useState("");
  const [specText, setSpecText] = useState("");
  const [createMissing, setCreateMissing] = useState(true);
  const [tab, setTab] = useState<"url" | "paste">("url");
  const [result, setResult] = useState<null | { summary: { endpoints: number; added: number; removed: number; changed: number }; drift: { added: unknown[]; removed: unknown[]; changed: unknown[] } }>(null);

  const importMut = useMutation({
    mutationFn: () => api.openapi.import({
      collection_id: collectionId,
      source_url: tab === "url" ? sourceUrl.trim() || undefined : undefined,
      spec_text: tab === "paste" ? specText.trim() || undefined : undefined,
      create_missing: createMissing,
    }),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ["openapi", collectionId] });
      qc.invalidateQueries({ queryKey: ["requests", collectionId] });
      qc.invalidateQueries({ queryKey: ["folders", collectionId] });
    },
  });

  const spec = existing.data?.spec;

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-4" style={{ background: "var(--backdrop)" }} onClick={onClose}>
      <div className="card w-full max-w-2xl shadow-[var(--shadow-strong)] p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Import OpenAPI spec</h2>
          <button className="btn" onClick={onClose}>Close</button>
        </div>

        {spec && (
          <div className="rounded-md border border-border bg-surface-2 p-3 text-xs space-y-1">
            <div><b className="text-text">Currently synced</b> · {spec.spec_version ?? "openapi"} · {spec.spec_hash.slice(0, 12)}</div>
            {spec.source_url && <div className="muted-strong break-all">{spec.source_url}</div>}
            <div className="muted-strong">Last sync: {new Date(spec.last_synced_at).toLocaleString()}</div>
            {spec.last_drift && (
              <div className="muted">
                Drift since last sync: <b className="text-text">+{(spec.last_drift.added as unknown[]).length}</b>
                {" / "}
                <b className="text-text">-{(spec.last_drift.removed as unknown[]).length}</b>
                {" / "}
                <b className="text-text">~{(spec.last_drift.changed as unknown[]).length}</b>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-1">
          {(["url", "paste"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={
                "px-3 py-1 rounded-sm text-xs font-medium " +
                (t === tab ? "bg-accent text-[var(--ink-on-accent)]" : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
              }
            >{t === "url" ? "From URL" : "Paste spec"}</button>
          ))}
        </div>

        {tab === "url" ? (
          <input
            className="input input-mono"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://api.example.com/openapi.json"
            spellCheck={false}
          />
        ) : (
          <textarea
            className="input input-mono min-h-[160px]"
            value={specText}
            onChange={(e) => setSpecText(e.target.value)}
            placeholder='{ "openapi": "3.0.0", "paths": { ... } }'
            spellCheck={false}
          />
        )}

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} />
          <span>Create requests for endpoints not already in this collection</span>
        </label>

        {importMut.isError && (
          <p className="text-xs text-danger">{String((importMut.error as Error).message)}</p>
        )}

        {result && (
          <div className="rounded-md border border-border bg-surface-2 p-3 text-sm space-y-1">
            <div className="font-semibold">Import summary</div>
            <div>{result.summary.endpoints} endpoints in spec · +{result.summary.added} added · -{result.summary.removed} missing · ~{result.summary.changed} changed</div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <LoadingButton className="btn-primary" pending={importMut.isPending} onClick={() => importMut.mutate()}>
            {spec ? "Sync" : "Import"}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}
